import React from 'react';
import { getCommentStatus, isMarkedAsOK, isOKStale } from './feedbackLogic';
import CommentThread from './CommentThread';
import CommentReplyForm from './CommentReplyForm';

/**
 * CommentCard - Generisk komponent för att visa kommentarer
 * Fungerar både för byggkonsult och kund beroende på context
 */
const CommentCard = ({
  commentKey,
  displayName,
  customerComment,           // Den ANDRA personens kommentar (incoming)
  consultantResponse,        // Mitt svar på deras kommentar
  customerComments,          // Alla incoming comments (hela objektet)
  byggkonsultResponses,      // Alla mina responses (hela objektet)
  consultantNeedsToReply,    // Om JAG behöver svara
  isExpanded,
  onToggle,
  newResponse,
  onResponseChange,
  onResponseSave,
  onResponseDelete,
  onResponseEdit,
  onEntryEdit,           // (commentKey, entryIndex, newText) → spara redigerad thread-entry
  onMarkAsOK,
  onUndoMarkOK,          // () => void — ångra kundens egen "Markerad som OK"
  hasUnsavedChanges,
  viewMode = 'consultant' // 'consultant' eller 'customer'
}) => {
  // Aliaser för läsbarhet - "incoming" är det JAG läser, "myResponses" är det JAG skriver
  const incomingComment = customerComment;
  const myResponse = consultantResponse;
  const incomingComments = customerComments;
  const myResponses = byggkonsultResponses;
  const iNeedToReply = consultantNeedsToReply;
  
  // Kolla om någon redan har markerat som OK — och att markeringen
  // fortfarande gäller (inte stale pga nytt svar från motparten)
  const iMarkedAsOK = isMarkedAsOK(commentKey, myResponses);
  const theyMarkedAsOK = isMarkedAsOK(commentKey, incomingComments);
  const anyHasOK = iMarkedAsOK || theyMarkedAsOK;
  const okIsStale = anyHasOK && isOKStale(commentKey, incomingComments, myResponses);
  const anyoneMarkedAsOK = anyHasOK && !okIsStale;

  // Extrahera incoming kommentar-data (den ANDRA personens meddelande)
  const incomingText = typeof incomingComment === 'object' 
    ? (incomingComment?.text || '') 
    : (incomingComment || '');
  const incomingAt = (typeof incomingComment === 'object' && (incomingComment?.commentedAt || incomingComment?.at)) 
    ? (incomingComment.commentedAt || incomingComment.at) 
    : null;
  const incomingName = (typeof incomingComment === 'object' && incomingComment?.author?.name) 
    ? incomingComment.author.name 
    : (viewMode === 'consultant' ? 'Kund' : 'Byggkonsult');

  // Extrahera mitt svar-data
  const myText = myResponse 
    ? (typeof myResponse === 'object' 
        ? (myResponse?.text || '') 
        : (myResponse || '')) 
    : '';
  const myAt = (myResponse && typeof myResponse === 'object' && (myResponse?.respondedAt || myResponse?.at)) 
    ? (myResponse.respondedAt || myResponse.at) 
    : null;
  const myName = (myResponse && typeof myResponse === 'object' && myResponse?.author?.name) 
    ? myResponse.author.name 
    : (viewMode === 'consultant' ? 'Byggkonsult' : 'Kund');

  // Extrahera trådar
  const incomingThread = Array.isArray(incomingComment?.thread) ? incomingComment.thread : [];
  const myThread = Array.isArray(myResponse?.thread) ? myResponse.thread : [];

  // Bygg meddelande-lista
  // Basmeddelanden (om inga trådar finns)
  const incomingBase = incomingText ? [{ 
    by: viewMode === 'consultant' ? 'customer' : 'consultant', 
    text: incomingText, 
    at: incomingAt, 
    author: { name: incomingName } 
  }] : [];
  const myBase = myText ? [{ 
    by: viewMode === 'consultant' ? 'consultant' : 'customer', 
    text: myText, 
    at: myAt, 
    author: { name: myName } 
  }] : [];
  
  // Om det finns trådar, använd bara dem. Annars använd grundkommentarerna.
  // Spara även myThreadIndex på consultant-meddelanden så vi kan referera
  // tillbaka till backend's byggkonsultResponses[key].thread-index vid edit.
  let messages = [];
  if (incomingThread.length > 0 || myThread.length > 0) {
    const incomingTagged = incomingThread.map(e => ({ ...e }));
    const myTagged = myThread.map((e, i) => ({ ...e, _myThreadIndex: i }));
    messages = [...incomingTagged, ...myTagged];
  } else {
    messages = [...incomingBase, ...myBase];
  }

  messages = messages.filter(Boolean).sort((a, b) => {
    const ta = a?.at ? new Date(a.at).getTime() : 0;
    const tb = b?.at ? new Date(b.at).getTime() : 0;
    return ta - tb;
  });

  // Bestäm om svarsformulär ska visas
  // För byggkonsult: visa om senaste meddelandet är från kund
  // För kund: visa om senaste meddelandet är från konsult
  // OBS: Om mitt svar är ett utkast (draft), visa formuläret ändå
  // OBS: Om newResponse har värde (redigering pågår), visa formuläret
  const myResponseIsDraft = myResponse && typeof myResponse === 'object' && myResponse.draft;
  const isEditing = newResponse && newResponse.trim().length > 0;

  // Om BK har påbörjat/sparat ett svar ska "Markera som OK" INTE gå att klicka —
  // annars skrivs det skrivna svaret över med "✓ Markerad som OK" (markCommentAsOK
  // ersätter svaret). Gäller både osparad text i fältet och sparat utkast.
  const hasResponseInProgress = Boolean(isEditing || myResponseIsDraft);
  const latest = messages[messages.length - 1];
  const showReplyForm = myResponseIsDraft || isEditing ? true : (latest
    ? (viewMode === 'consultant' ? latest.by !== 'consultant' : latest.by !== 'customer')
    : false);

  return (
    <div className="border border-blue-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div
        className="p-2 sm:p-3 cursor-pointer select-none bg-blue-50"
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-controls={`feedback_item_${commentKey}`}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        {/* Namn + status + chevron */}
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-gray-900 truncate">{displayName}</div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Status badge */}
            <span className="inline-flex items-center gap-1 text-xs text-gray-700">
              {(() => {
                const status = getCommentStatus(commentKey, incomingComments, myResponses);
                const isDraftStatus = status === 'Utkast' || status === 'Kunds utkast';
                const dotColor = isDraftStatus ? 'bg-amber-400' : (iNeedToReply && !anyoneMarkedAsOK) ? 'bg-red-400' : 'bg-green-400';
                return <span className={`w-2 h-2 rounded-full ${dotColor}`}></span>;
              })()}
              {getCommentStatus(commentKey, incomingComments, myResponses)}
            </span>

            {/* Markera som OK knapp — dold på mobil, visas under istället.
                Döljs om BK påbörjat ett svar (skydd mot att skriva över svaret). */}
            {iNeedToReply && !anyoneMarkedAsOK && !hasResponseInProgress && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkAsOK();
                }}
                className="hidden sm:inline-flex px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border"
              >
                Markera som OK
              </button>
            )}

            {/* Ångra "Markerad som OK" — kunden kan ta tillbaka sin egen markering */}
            {viewMode === 'customer' && iMarkedAsOK && !okIsStale && onUndoMarkOK && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onUndoMarkOK();
                }}
                className="hidden sm:inline-flex px-2 py-1 text-xs bg-amber-50 hover:bg-amber-100 text-amber-700 rounded border border-amber-200"
              >
                Ångra OK
              </button>
            )}

            {/* Chevron icon */}
            <svg
              className={`w-4 h-4 text-gray-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {/* Markera som OK — knapp under punkten på mobil */}
        {iNeedToReply && !anyoneMarkedAsOK && !hasResponseInProgress && (
          <div className="mt-1.5 sm:hidden">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onMarkAsOK();
              }}
              className="px-2 py-0.5 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border"
            >
              Markera som OK
            </button>
          </div>
        )}

        {/* Ångra "Markerad som OK" — mobil */}
        {viewMode === 'customer' && iMarkedAsOK && !okIsStale && onUndoMarkOK && (
          <div className="mt-1.5 sm:hidden">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onUndoMarkOK();
              }}
              className="px-2 py-0.5 text-xs bg-amber-50 hover:bg-amber-100 text-amber-700 rounded border border-amber-200"
            >
              Ångra OK
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      {isExpanded && (
        <div id={`feedback_item_${commentKey}`} className="px-2 sm:px-3 pb-2 sm:pb-3 bg-white">
          <CommentThread
            messages={messages}
            viewMode={viewMode}
            onEditEntry={onEntryEdit ? async (displayedIndex, newText) => {
              const msg = messages[displayedIndex];
              if (!msg || msg.by !== 'consultant' || typeof msg._myThreadIndex !== 'number') return;
              await onEntryEdit(commentKey, msg._myThreadIndex, newText);
            } : undefined}
          />

          {/* Redigera/ta bort-knappar för BK:s egna svar (draft eller publicerade) */}
          {/* Visas oavsett om svarsformulär syns — så BK kan redigera alla sina */}
          {/* tidigare svar (även när kund svarat tillbaka eller BK har en draft) */}
          {viewMode === 'consultant' && myResponse && !anyoneMarkedAsOK && (onResponseDelete || onResponseEdit) && (
            <div className="flex items-center gap-2 mt-2">
              {onResponseEdit && (
                <button
                  type="button"
                  onClick={onResponseEdit}
                  className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Redigera
                </button>
              )}
              {onResponseDelete && (
                <button
                  type="button"
                  onClick={onResponseDelete}
                  className="text-xs text-red-500 hover:text-red-700 hover:underline"
                >
                  Ta bort
                </button>
              )}
            </div>
          )}

          {showReplyForm && (
            <CommentReplyForm
              commentKey={commentKey}
              value={newResponse}
              onChange={onResponseChange}
              onSave={onResponseSave}
              hasUnsavedChanges={hasUnsavedChanges}
              viewMode={viewMode}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default CommentCard;

